import { z } from 'zod';

import { simplePaginationSchema } from '../../../shared/source/data-contract/pagination';
import { languageCodeSchema } from '../../../shared/source/internationalization';
import { jobArchitectureProfileReviewStateSchema } from '../job-profile/job-profile-schema';
import { personSchema } from '../position-management/person';

export const jobArchitectureItemStatisticsSchema = z.object({
    approvedLevels: z.number().int().nonnegative().default(0),
    approvedRoles: z.number().int().nonnegative().default(0),
    clustersCount: z.number().int().nonnegative().default(0),
    expected: z.number().int().nonnegative().default(0),
    familiesCount: z.number().int().nonnegative().default(0),
    levelsCount: z.number().int().nonnegative().default(0),
    profiles: z.number().int().nonnegative().default(0),
    publishedLevels: z.number().int().nonnegative().default(0),
    publishedRoles: z.number().int().nonnegative().default(0),
    reviewed: z.number().int().nonnegative().default(0),
    reviewerLevels: z.number().int().nonnegative().default(0),
    reviewerRoles: z.number().int().nonnegative().default(0),
    reviewers: z.number().int().nonnegative().default(0),
    rolesCount: z.number().int().nonnegative().default(0),
});

export type JobArchitectureItemStatistics = z.infer<typeof jobArchitectureItemStatisticsSchema>;

const detectionStateSchema = z.enum(['queued', 'started', 'finished', 'failed']).nullable().optional();

export const jobArchitectureGenerationInfoSchema = z.object({
    inProgress: z.boolean(),
    skillDetectionState: detectionStateSchema,
    translationState: detectionStateSchema,
});

export type JobArchitectureGenerationInfo = z.infer<typeof jobArchitectureGenerationInfoSchema>;

const jobArchitectureGenerationInfoWithDebugSchema = jobArchitectureGenerationInfoSchema.extend({
    createdAt: z.string(),
    debugInformation: z.record(z.string(), z.unknown().transform(String)).optional(),
    state: z.enum(['queued', 'started', 'finished', 'failed']),
    updatedAt: z.string(),
});

export const jobArchitectureDebugInfoSchema = z.object({
    generationInfo: jobArchitectureGenerationInfoWithDebugSchema.optional(),
    profileGenerationInfo: jobArchitectureGenerationInfoWithDebugSchema.optional(),
});

export type JobArchitectureDebugInformation = z.infer<typeof jobArchitectureDebugInfoSchema>;

export const jobArchitectureSchema = z.object({
    id: z.string(),
    actions: z.object({ canEdit: z.boolean() }).optional(),
    childCount: z.number().int().nonnegative(),
    description: z.string().optional(),
    generationInfo: jobArchitectureGenerationInfoSchema.optional(),
    statistics: jobArchitectureItemStatisticsSchema,
    title: z.string(),
    translations: z
        .object({
            description: z.string().optional(),
            language: languageCodeSchema,
            title: z.string(),
        })
        .array(),
});

export const jobArchitecturesSchema = jobArchitectureSchema.array();

export type JobArchitecture = z.infer<typeof jobArchitectureSchema>;

export type JobArchitectures = z.infer<typeof jobArchitecturesSchema>;

const selectedItemStateSchema = z.union([z.literal('default'), z.literal('selected'), z.literal('selectedPath')]);

export const jobArchitectureItemSchema = z.object({
    id: z.string(),
    architectureId: z.string(),
    childCount: z.number().int().nonnegative().default(0),
    description: z.string().optional(),
    parentId: z.string().nullable(),
    selectedState: selectedItemStateSchema.default('default'),
    statistics: jobArchitectureItemStatisticsSchema.default({
        approvedLevels: 0,
        approvedRoles: 0,
        clustersCount: 0,
        expected: 0,
        familiesCount: 0,
        levelsCount: 0,
        profiles: 0,
        publishedLevels: 0,
        publishedRoles: 0,
        reviewed: 0,
        reviewerLevels: 0,
        reviewerRoles: 0,
        reviewers: 0,
        rolesCount: 0,
    }),
    title: z.string(),
    translations: z
        .object({
            description: z.string().optional(),
            language: languageCodeSchema,
            title: z.string(),
        })
        .array(),
});

export type JobArchitectureItemType = 'job-cluster' | 'job-family' | 'job-level' | 'job-role';

type SelectedItemState = z.infer<typeof selectedItemStateSchema>;

export type JobArchitectureItem<T extends { readonly type: JobArchitectureItemType }> = T &
    z.infer<typeof jobArchitectureItemSchema> & { readonly selectedState: SelectedItemState };

export const companySizeSchema = z.union([
    z.literal('small'),
    z.literal('medium'),
    z.literal('large'),
    z.literal('huge'),
    z.literal('gigantic'),
]);

export type CompanySize = z.infer<typeof companySizeSchema>;

export const jobArchitectureMetadataSchema = z.object({
    businessUnits: z.string().array(),
    companyDescription: z.string(),
    companyExecutiveRolesInFamily: z.boolean(),
    companyName: z.string().optional(),
    companyPriorities: z.string().optional(),
    companySize: companySizeSchema,
    expectedNumberOfProfiles: z.number().nonnegative().optional(),
});

export type JobArchitectureMetadata = z.infer<typeof jobArchitectureMetadataSchema>;

const jobArchitectureSearchItemPathSchema = z.record(z.string(), z.uuid());

export const jobProfileReviewerSchema = z.object({
    id: z.uuid(),
    emailAddress: z.string().nullable(),
    firstName: z.string(),
    lastName: z.string(),
    state: jobArchitectureProfileReviewStateSchema,
});

export const jobArchitectureSearchItemSchema = z.object({
    id: z.uuid(),
    childCount: z.number().int().nonnegative(),
    externalId: z.string().nullable(),
    level: z.enum(['family', 'cluster', 'role', 'level', 'root']),
    path: jobArchitectureSearchItemPathSchema,
    reviewer: jobProfileReviewerSchema.nullable(),
    state: z.enum(['draft', 'active', 'archived']).nullable(),
    statistics: jobArchitectureItemStatisticsSchema,
    title: z.string(),
});

export const jobArchitectureSearchResponseSchema = z.object({
    data: z.array(jobArchitectureSearchItemSchema),
    pagination: simplePaginationSchema,
});

export type JobProfileReviewer = z.infer<typeof jobProfileReviewerSchema>;
export type JobArchitectureSearchItem = z.infer<typeof jobArchitectureSearchItemSchema>;
export type JobArchitectureSearchResponse = z.infer<typeof jobArchitectureSearchResponseSchema>;

const reviewerFilterSchema = z.object({
    field: z.literal('reviewerId'),
    type: z.literal('user'),
    values: z.array(personSchema.nullable()),
});

export const jobArchitectureFiltersResponseSchema = z.object({
    filters: z.array(
        z.union([
            reviewerFilterSchema,
            z.object({
                field: z.string(),
                type: z.string(),
                values: z.array(z.unknown().nullable()).optional(),
            }),
        ]),
    ),
});

export type ReviewerFilter = z.infer<typeof reviewerFilterSchema>;
export type JobArchitectureFiltersResponse = z.infer<typeof jobArchitectureFiltersResponseSchema>;
